import { useCallback, useEffect, useState } from "react";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TestingOverview } from "@/components/admin/testing/TestingOverview";
import { TestingTesters } from "@/components/admin/testing/TestingTesters";
import { TestingAnnounce } from "@/components/admin/testing/TestingAnnounce";
import { TestingEvents } from "@/components/admin/testing/TestingEvents";
import { TestingProgramSettings } from "@/components/admin/testing/TestingProgramSettings";
import { TestingIntegration } from "@/components/admin/testing/TestingIntegration";

const TABS = [["overview", "Bottleneck Finder"], ["testers", "Testers"], ["announce", "Announce"], ["events", "Crashes & Bugs"], ["program", "Program"], ["integration", "UE5 Integration"]];

/* Test Program command center — recruit, approve, deliver kits, and let the telemetry tell us what to optimise. */
export default function AdminTesting() {
  const [tab, setTab] = useState("overview");
  const [prog, setProg] = useState(null);
  const [ov, setOv] = useState(null);

  const load = useCallback(() => Promise.all([api.get("/admin/testing/program"), api.get("/admin/testing/overview")])
    .then(([a, b]) => { setProg(a.data); setOv({ ...b.data, ai_brief: a.data.program.ai_brief }); })
    .catch(() => toast.error("Could not load the test program")), []);
  useEffect(() => { load(); }, [load]);

  const counts = prog?.counts || {};
  return (
    <section data-testid="admin-testing-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-nc-orange" /> Test Program
          {prog && <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 border ${prog.program.status === "open" ? "text-nc-green border-nc-green/40" : "text-zinc-400 border-white/15"}`}>{prog.program.status}</span>}
        </h2>
        <div className="flex gap-1 flex-wrap">
          {TABS.map(([id, label]) => (
            <button key={id} data-testid={`testing-tab-${id}`} onClick={() => setTab(id)} className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border transition-colors ${tab === id ? "bg-nc-orange border-nc-orange text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
              {label}{id === "testers" && counts.waitlisted ? ` (${counts.waitlisted} waiting)` : ""}{id === "events" && ov?.events ? ` (${(ov.events.crash || 0) + (ov.events.bug || 0)})` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {!prog ? <Loader2 className="w-5 h-5 text-nc-orange animate-spin" /> : (
          <>
            {tab === "overview" && <TestingOverview ov={ov} reload={load} />}
            {tab === "testers" && <TestingTesters counts={counts} keysAvailable={prog.program.steam_keys_available} onChange={load} />}
            {tab === "announce" && <TestingAnnounce program={prog.program} onChange={load} />}
            {tab === "events" && <TestingEvents />}
            {tab === "program" && <TestingProgramSettings key={prog.program.updated_at} program={prog.program} onSaved={load} />}
            {tab === "integration" && <TestingIntegration />}
          </>
        )}
      </div>
    </section>
  );
}
